import { Button } from '@/components/ui/button'
import { DateRangePicker } from '@/components/ui/DateRangePicker'
import { SearchInput } from '@/components/ui/search-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDebouncedInput } from '@/hooks/useDebouncedInput'
import { staffUserName, useStaffUsers } from '@/models/user'

const ANY_REP = '__any__'

export interface TelephonySearchValues {
  q: string
  userId: string
  dateFrom: string
  dateTo: string
}

interface Props extends TelephonySearchValues {
  /** What the search matches, for the field's tooltip. */
  searchHint: string
  onChange: (updates: Partial<TelephonySearchValues>) => void
}

/** Search, rep and date filters shared by the All SMS and All Calls pages. */
export function TelephonySearchFilters({ q, userId, dateFrom, dateTo, searchHint, onChange }: Props) {
  const { staffUsers } = useStaffUsers()
  const { inputValue, setInputValue } = useDebouncedInput(q, (value) => onChange({ q: value }))

  return (
    <>
      <SearchInput
        value={inputValue}
        onValueChange={setInputValue}
        placeholder="Search text, number, lead or customer…"
        title={searchHint}
        aria-label="Search"
        className="max-w-none sm:basis-full"
      />

      <Select
        value={userId || ANY_REP}
        onValueChange={(value) => onChange({ userId: value === ANY_REP ? '' : value })}
      >
        <SelectTrigger className="w-full sm:w-48">
          <SelectValue placeholder="Any rep" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY_REP}>Any rep</SelectItem>
          {staffUsers.map((user) => (
            <SelectItem key={user.id} value={user.id}>
              {staffUserName(user)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex gap-2">
        <DateRangePicker
          start={dateFrom}
          end={dateTo}
          placeholder="Any date"
          onChange={(start, end) => onChange({ dateFrom: start, dateTo: end })}
        />
        {(dateFrom || dateTo) && (
          <Button variant="ghost" onClick={() => onChange({ dateFrom: '', dateTo: '' })}>
            Clear dates
          </Button>
        )}
      </div>
    </>
  )
}
